/**
 * Main application component
 * Orchestrates vocabulary display and motion detection functionality
 */
import { useState, useCallback, useEffect } from 'react';
import { ThemeProvider } from 'next-themes';
import { Toaster } from './components/ui/sonner';
import { LoadingSpinner } from './components/ui/loading-spinner';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { WordDisplay } from './components/WordDisplay';
import { useShakeDetection } from './hooks/useShakeDetection';
import { useGetRandomWord } from './hooks/useVocabulary';
import { useVocabularyService } from './hooks/useVocabularyService';
import { toast } from 'sonner';
import { ANIMATION_DURATION_MS } from './lib/constants';
import type { VocabularyEntry } from './types';

export default function App() {
  const [currentWord, setCurrentWord] = useState<VocabularyEntry | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasShownPermissionPrompt, setHasShownPermissionPrompt] = useState(false);

  const { isReady, isInitializing } = useVocabularyService();
  const { mutate: getRandomWord, isPending } = useGetRandomWord();

  // Fetch and display new word with animation
  const handleNewWord = useCallback(() => {
    if (isPending || isAnimating || !isReady) return;

    setIsAnimating(true);

    getRandomWord(void 0, {
      onSuccess: (data) => {
        if (data) {
          setCurrentWord(data);
          // Reset animation after duration
          setTimeout(() => setIsAnimating(false), ANIMATION_DURATION_MS);
        } else {
          toast.error('No words available in vocabulary');
          setIsAnimating(false);
        }
      },
      onError: (error) => {
        console.error('Failed to fetch word:', error);
        toast.error('Failed to fetch word. Please try again.');
        setIsAnimating(false);
      },
    });
  }, [getRandomWord, isPending, isAnimating, isReady]);

  // Setup shake detection when service is ready
  const { isSupported, permissionState, requestPermission } = useShakeDetection({
    onShake: handleNewWord,
    enabled: isReady && !isInitializing,
  });

  // Load initial word on mount
  useEffect(() => {
    if (isReady && !isInitializing && !currentWord) {
      handleNewWord();
    }
  }, [isReady, isInitializing, currentWord, handleNewWord]);

  // Request motion sensor permission (iOS 13+ requirement)
  const handleRequestPermission = async () => {
    setHasShownPermissionPrompt(true);
    const granted = await requestPermission();
    if (granted) {
      toast.success('Motion detection enabled successfully');
    } else {
      toast.error('Permission denied. You can still use the button.');
    }
  };

  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5">
        <Header />

        {isInitializing || !isReady ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <LoadingSpinner size="lg" className="mx-auto mb-4" />
              <p className="text-muted-foreground">Initializing application...</p>
            </div>
          </div>
        ) : (
          <WordDisplay
            word={currentWord}
            isAnimating={isAnimating}
            isPending={isPending}
            isSupported={isSupported}
            permissionState={permissionState}
            hasShownPermissionPrompt={hasShownPermissionPrompt}
            onRequestPermission={handleRequestPermission}
            onNewWord={handleNewWord}
          />
        )}

        <Footer />
      </div>
      <Toaster />
    </ThemeProvider>
  );
}

